import * as THREE from 'three';
import type { SkyRig } from './sky';
import type { GoogleTiles } from './googleTiles';
import { sunAnglesForSlider } from './solar';

// Drives every light-dependent part of the scene from one real sun position.
//
// Scene axes come from the geo-referenced tiles frame: +Z is north and +X is
// west (ReorientationPlugin at azimuth 0). A compass azimuth A therefore maps
// to three.js spherical theta = -A, with phi = 90deg - elevation.

// Fog palette per phase, matched to the Sky addon's horizon so the terrain
// dissolves into haze into sky with no seam. Warmer and paler than a temperate
// sky: this is a dusty plateau, not a coastal city.
// Warm, not blue. The haze is graded to the sand it meets so the dune field
// dissolves into the horizon; a cool fog left the ground ending against a blue
// band, which is the one seam a large terrain cannot hide.
const FOG_DAY = new THREE.Color('#c4b394');
const FOG_DUSK = new THREE.Color('#d98f57');
const FOG_NIGHT = new THREE.Color('#0a1020');
const KEY_NOON = new THREE.Color('#fff6ea');
const KEY_LOW = new THREE.Color('#ffcf9e');

export interface DayNightTargets {
    scene: THREE.Scene;
    sky: SkyRig;
    key: THREE.DirectionalLight;
    ambient: THREE.AmbientLight;
    tiles: GoogleTiles | null;
}

export class DayNightController {
    private t = 0.5;
    private sunDir = new THREE.Vector3();
    private fog = new THREE.Color();
    private targets: DayNightTargets;

    /** Unit vector toward the sun, updated every apply(). */
    readonly sunDirection = new THREE.Vector3();
    /** Sun elevation in degrees; negative after sunset. */
    sunElevation = 0;
    /** 0 = full day, 1 = full night. */
    night = 0;

    constructor(targets: DayNightTargets) {
        this.targets = targets;
    }

    setTiles(tiles: GoogleTiles | null): void {
        this.targets.tiles = tiles;
    }

    get time(): number {
        return this.t;
    }

    /** t: 0 = 05:00 local, 1 = 23:00 local (see solar.ts). */
    apply(t: number): void {
        this.t = t;
        const { scene, sky, key, ambient, tiles } = this.targets;
        const { elev, azim } = sunAnglesForSlider(t);

        const phi = THREE.MathUtils.degToRad(90 - elev);
        const theta = -THREE.MathUtils.degToRad(azim);
        this.sunDir.setFromSphericalCoords(1, phi, theta);
        this.sunDirection.copy(this.sunDir);
        this.sunElevation = elev;

        const daylight = THREE.MathUtils.smoothstep(elev, -10, 12);
        const night = 1 - daylight;
        this.night = night;
        // Peaks while the sun crosses the horizon band; zero at noon and deep night.
        const dusk =
            THREE.MathUtils.smoothstep(elev, -12, 4) * (1 - THREE.MathUtils.smoothstep(elev, 4, 28));

        // One sun source: atmosphere, clouds, fog colour and key light all follow it.
        sky.setSun(this.sunDir);
        sky.setLighting(daylight, dusk);

        this.fog.lerpColors(FOG_NIGHT, FOG_DAY, daylight).lerp(FOG_DUSK, dusk * 0.65);
        if (scene.fog instanceof THREE.Fog) scene.fog.color.copy(this.fog);
        // Horizon band tracks the fog so backdrop and haze stay one gradient.
        sky.setHorizon(this.fog);

        // The key light rides a fixed-radius arc around the model so its shadow
        // frustum (sized for the model, not the city) always contains it.
        const keyDistance = 60;
        key.position.copy(this.sunDir).multiplyScalar(keyDistance);
        key.position.y = Math.max(key.position.y, 2);
        // Lifted from 3.2: the desert reads as a bright place, and the model was
        // sitting a stop under the photoreal terrain around it.
        key.intensity = 4.2 * daylight;
        key.color.lerpColors(KEY_LOW, KEY_NOON, THREE.MathUtils.clamp(elev / 40, 0, 1));

        // Ambient carries the blue-hour fill so the model never goes fully black,
        // and opens up the shadowed courtyards by day.
        ambient.intensity = THREE.MathUtils.lerp(0.18, 0.46, daylight);

        // Keep the environment fill low by day so the sun carries the contrast;
        // a high fill washes the facades flat.
        scene.environmentIntensity = THREE.MathUtils.lerp(0.5, 2.6, daylight);

        tiles?.setDaylight(daylight);
    }
}
